/**
 * Daily Replenishment Plan Report Component
 */
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Grid,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import api from '../../services/api';
import { ReportResponse, DailyReplenishmentPlanItem } from '../../types/reports';

const DailyReplenishmentPlan: React.FC = () => {
  const [targetDate, setTargetDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [selectedStore, setSelectedStore] = useState('');

  // Fetch stores for filter
  const { data: stores } = useQuery({
    queryKey: ['stores'],
    queryFn: async () => {
      const response = await api.getStores();
      return response.data;
    },
  });

  // Fetch report data
  const {
    data: reportData,
    isLoading,
    error,
    refetch,
  } = useQuery<ReportResponse<DailyReplenishmentPlanItem>>({
    queryKey: ['dailyReplenishmentPlan', targetDate, selectedStore],
    queryFn: async () => {
      const response = await api.getDailyReplenishmentPlan({
        target_date: targetDate,
        store_id: selectedStore || undefined,
      });
      return response.data;
    },
  });

  const handleExport = async (format: 'csv' | 'json') => {
    try {
      const response =
        format === 'csv'
          ? await api.exportReportCSV('daily_replenishment_plan', {
              target_date: targetDate,
              store_id: selectedStore || undefined,
            })
          : await api.exportReportJSON('daily_replenishment_plan', {
              target_date: targetDate,
              store_id: selectedStore || undefined,
            });

      // Create download link
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `daily_replenishment_${targetDate}.${format}`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      // Export error - silently handled
    }
  };

  const getPriorityClass = (priority: number): string => {
    if (priority >= 100) return 'status-badge status-badge--danger';
    if (priority >= 75) return 'status-badge status-badge--warning';
    if (priority >= 50) return 'status-badge status-badge--neutral';
    return 'status-badge status-badge--neutral';
  };

  const getPriorityLabel = (priority: number): string => {
    if (priority >= 100) return 'Critical';
    if (priority >= 75) return 'High';
    if (priority >= 50) return 'Medium';
    return 'Low';
  };

  const getStatusClass = (status: string): string => {
    if (status === 'approved') return 'status-badge status-badge--success';
    return 'status-badge status-badge--neutral';
  };

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            type="date"
            label="Target Date"
            value={targetDate}
            onChange={(e) => setTargetDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>Store Filter</InputLabel>
            <Select
              value={selectedStore}
              label="Store Filter"
              onChange={(e) => setSelectedStore(e.target.value)}
            >
              <MenuItem value="">All Stores</MenuItem>
              {stores?.map((store: any) => (
                <MenuItem key={store.store_id} value={store.store_id}>
                  {store.store_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => refetch()}
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => handleExport('csv')}
            >
              Export CSV
            </Button>
          </Box>
        </Grid>
      </Grid>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Error loading report: {(error as Error).message}
        </Alert>
      )}

      {reportData && (
        <>
          <Box sx={{ mb: 2, display: 'flex', gap: 3 }}>
            <Typography sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
              Generated: {new Date(reportData.metadata.generated_at).toLocaleString()}
            </Typography>
            <Typography sx={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
              Total Orders: <strong>{reportData.metadata.record_count}</strong>
            </Typography>
          </Box>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Priority</TableCell>
                  <TableCell>Store</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell>SKU</TableCell>
                  <TableCell>Item Status</TableCell>
                  <TableCell align="right">Current Stock</TableCell>
                  <TableCell align="right">In Transit</TableCell>
                  <TableCell align="right">Quantity Ordered</TableCell>
                  <TableCell>Expected Delivery</TableCell>
                  <TableCell>Order Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData.data.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      No replenishment orders for this date
                    </TableCell>
                  </TableRow>
                ) : (
                  reportData.data.map((item: DailyReplenishmentPlanItem) => (
                    <TableRow key={item.order_id}>
                      <TableCell>
                        <span className={getPriorityClass(item.priority)}>
                          {getPriorityLabel(item.priority)}
                        </span>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500, color: 'var(--color-text-primary)' }}>{item.store_name}</TableCell>
                      <TableCell>{item.product_name}</TableCell>
                      <TableCell sx={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)' }}>{item.sku}</TableCell>
                      <TableCell sx={{ py: '6px', px: '8px' }}>
                        {(item as any).lifecycle_stage ? (
                          <Chip
                            label={(item as any).lifecycle_stage}
                            size="small"
                            color={
                              (item as any).lifecycle_stage === 'NEW' ? 'info' :
                              (item as any).lifecycle_stage === 'GROWTH' ? 'success' :
                              (item as any).lifecycle_stage === 'DECLINING' ? 'warning' :
                              ((item as any).lifecycle_stage === 'PHASE_OUT' || (item as any).lifecycle_stage === 'DISCONTINUED') ? 'error' :
                              'default'
                            }
                            variant="outlined"
                            sx={{ fontSize: '10px', height: 20 }}
                          />
                        ) : (
                          <Typography sx={{ fontSize: '10px', color: 'var(--color-text-muted)' }}>—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{item.current_stock}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{item.in_transit}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                        {item.quantity_ordered}
                      </TableCell>
                      <TableCell>
                        {item.expected_delivery
                          ? new Date(item.expected_delivery).toLocaleDateString()
                          : 'TBD'}
                      </TableCell>
                      <TableCell>
                        <span className={getStatusClass(item.status)}>
                          {item.status}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};

export default DailyReplenishmentPlan;
